/* eslint-disable import/no-extraneous-dependencies */
import { Suspense } from 'react';
import {
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import {
  getModulePath,
  LEAGUE_MODULE_NAMES,
  LeagueProvider,
  overwriteRoutePaths,
} from '@leagueplatform/core';
import { GenericMasonryPage } from '@leagueplatform/masonry';
import {
  FullPageError,
  LoadingIndicator,
} from '@leagueplatform/web-common-components';
import { useOnlineStatus } from '@leagueplatform/web-common';
import { GDSThemeProvider, Spinner } from '@leagueplatform/genesis-core';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { routesConfig } from 'config';
import { scanHealthTheme } from 'theme/scan-theme';
import { ROUTE_PATHS } from 'common/constants/constants';
import { FEATURE_FLAGS } from 'common/firebase';
import { useGetLeagueConfig } from 'hooks/use-get-league-config.hook';
import { useFeatureFlags } from 'hooks/use-feature-flags/use-feature-flags.hook';
import {
  LandingPage,
  CareCollectionsRoutes,
  AboutMeRoutes,
  HealthProfileRoutes,
  Home,
  AccountInfo,
  HealthJourneyRoutes,
  MessagingRoutes,
  PriorAuthorizations,
  PriorAuthorizationsDetails,
  Prescriptions,
  PrescriptionDetails,
  RefillScore,
  RefillScoreDetails,
  ContactScan,
  ChangeDoctor,
  HealthyRewardsFaq,
} from 'components/lazy-routes.component';
import {
  StyledCareDiscoveryRoutes,
  StyledWalletRoutes,
  StyledCareDocumentsRoutes,
  StyledHealthJourneyRoutes,
} from 'components/styled-routes.component';
import { AuthViewLogin, AuthViewLogout } from 'pages/auth/auth.pages';
import { VerifyEmail } from 'pages/verify-email.page';
import { AuthorizedRoutes } from 'components/authorized-routes/authorized-routes.component';
import { GenericSkeletonPage } from 'components/skeleton-page/skeleton-page.component';
import { CommunicationPreferencesPage } from 'pages/communication-preferences/communication-preferences.page';
import { DocumentPreferencesPage } from 'pages/document-preferences/document-preferences.page';
import { AboutPage } from 'pages/about/about.page';
import {
  GenericErrorPage,
  PageNotFoundErrorPage,
} from 'components/error-page/error-page.component';
import { BasePathRedirect } from 'components/base-path-redirect/base-path-redirect.component';
import { ExerciseYourBrain } from 'pages/exercise-your-brain/exercise-your-brain.page';
import { Onboarding } from 'components/onboarding/onboarding.container';
import { GenericMasonryPageWrapper } from 'components/generic-masonry-page-wrapper/generic-masonry-page-wrapper.component';
import { ExitWarningContextProvider } from 'components/modals/exit-warning/exit-warning.context';
import { RemoteConfigProvider } from 'components/remote-config-provider/remote-config-provider.component';

overwriteRoutePaths(routesConfig);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
    },
  },
});

/**
 * AppShell is the root layout route element.
 * It provides all application-level context providers (LeagueProvider,
 * QueryClientProvider, etc.) and renders child routes via <Outlet />.
 *
 * This ensures LeagueProvider is INSIDE the router context,
 * which is required by SDK 3.0.
 */
const AppShell = () => {
  const { isLoading: isConfigLoading, config } = useGetLeagueConfig();
  const { isOffline, isOnline } = useOnlineStatus();

  if (isConfigLoading) {
    return (
      <GDSThemeProvider theme={scanHealthTheme}>
        <Spinner
          loading
          loadingText="Loading..."
          css={{
            display: 'block',
            width: '100vw',
            height: '100vh',
            alignContent: 'center',
            textAlign: 'center',
          }}
        />
      </GDSThemeProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <LeagueProvider config={config}>
        <ErrorBoundary fallback={<GenericErrorPage />}>
          <RemoteConfigProvider>
            <Suspense fallback={<LoadingIndicator />}>
              {isOffline && (
                <FullPageError error={null} route={ROUTE_PATHS.HOME} />
              )}
              {isOnline && (
                <ExitWarningContextProvider>
                  <Outlet />
                </ExitWarningContextProvider>
              )}
              <ReactQueryDevtools position="bottom-right" />
            </Suspense>
          </RemoteConfigProvider>
        </ErrorBoundary>
      </LeagueProvider>
    </QueryClientProvider>
  );
};

/**
 * FeatureFlagGuard renders children only when the given feature flag is enabled.
 * Falls back to the 404 page when disabled.
 */
const FeatureFlagGuard = ({
  flag,
  children,
}: {
  flag: FEATURE_FLAGS;
  children: React.ReactNode;
}) => {
  const { featureFlags, isLoading } = useFeatureFlags([flag]);

  if (isLoading) return <LoadingIndicator />;
  if (!featureFlags[flag]) return <PageNotFoundErrorPage />;

  return <>{children}</>;
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppShell />} errorElement={<GenericErrorPage />}>
      <Route path={ROUTE_PATHS.VERIFY_EMAIL} element={<VerifyEmail />} />
      <Route path={ROUTE_PATHS.SIGN_IN} element={<AuthViewLogin />} />
      <Route path={ROUTE_PATHS.SIGN_OUT} element={<AuthViewLogout />} />
      <Route path="/" element={<LandingPage />} />
      <Route element={<AuthorizedRoutes />}>
        {/* Feature-flagged route: guarded instead of conditionally defined */}
        <Route
          path={ROUTE_PATHS.CHANGE_DOCTOR}
          element={
            <FeatureFlagGuard flag={FEATURE_FLAGS.CHANGE_PCP}>
              <ChangeDoctor />
            </FeatureFlagGuard>
          }
        />
        {/* Authorized views with top nav and global footer */}
        <Route element={<Onboarding />}>
          {/* Remove all /member paths - they're hardcoded in some SDKs but the app doesn't handle them anymore. */}
          <Route
            path={`${ROUTE_PATHS.MEMBER_REDIRECT}/*`}
            element={<BasePathRedirect targetBasePath="" />}
          />
          <Route
            path={ROUTE_PATHS.REFILL_SCORE_DETAILS}
            element={<RefillScoreDetails />}
            key="refill-score-details"
          />
          <Route
            path={ROUTE_PATHS.REFILL_SCORE}
            element={<RefillScore />}
            key="refill-score"
          />
          <Route
            path={ROUTE_PATHS.PRESCRIPTIONS}
            element={<Prescriptions />}
            key="prescriptions"
          />
          <Route
            path={ROUTE_PATHS.PRESCRIPTION_DETAILS}
            element={<PrescriptionDetails />}
            key="prescription-details"
          />
          <Route
            path={ROUTE_PATHS.PRIOR_AUTHORIZATIONS}
            element={<PriorAuthorizations />}
            key="prior-authorizations"
          />
          <Route
            path={ROUTE_PATHS.PRIOR_AUTHORIZATIONS_DETAILS}
            element={<PriorAuthorizationsDetails />}
            key="prior-authorizations-details"
          />
          <Route
            path={ROUTE_PATHS.CONTACT_SCAN}
            element={<ContactScan />}
            key="contact-scan"
          />
          <Route
            path={ROUTE_PATHS.HEALTHY_REWARDS_FAQ}
            element={<HealthyRewardsFaq />}
            key="healthy-rewards-faq"
          />
          <Route path={ROUTE_PATHS.HOME} element={<Home />} key="home" />
          <Route
            path={ROUTE_PATHS.ACCOUNT_INFO}
            element={
              <Suspense fallback={<GenericSkeletonPage />}>
                <AccountInfo />
              </Suspense>
            }
          />
          <Route
            path={ROUTE_PATHS.COMMUNICATION_PREFERENCES}
            element={<CommunicationPreferencesPage />}
          />
          <Route
            path={ROUTE_PATHS.EXERCISE_YOUR_BRAIN}
            element={<ExerciseYourBrain />}
          />
          <Route path={ROUTE_PATHS.ABOUT} element={<AboutPage />} />
          <Route
            path={ROUTE_PATHS.DOCUMENT_PREFERENCES()}
            element={
              <Suspense fallback={<GenericSkeletonPage />}>
                <DocumentPreferencesPage />
              </Suspense>
            }
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.careCollections)}/*`}
            element={<CareCollectionsRoutes />}
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.careDiscovery)}/*`}
            element={<StyledCareDiscoveryRoutes />}
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.wallet)}/*`}
            element={<StyledWalletRoutes />}
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.aboutMe)}/*`}
            element={<AboutMeRoutes />}
          />
          {/* About Me in Masonry v.1 has metrics hardcoded to /health-profile.
              The metrics are now under /about-me. */}
          <Route
            path={`${getModulePath(
              LEAGUE_MODULE_NAMES.healthProfile,
            )}/insights/*`}
            element={
              <BasePathRedirect
                targetBasePath={getModulePath(LEAGUE_MODULE_NAMES.aboutMe)}
              />
            }
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.healthProfile)}/*`}
            element={<HealthProfileRoutes />}
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.messaging)}/*`}
            element={<MessagingRoutes />}
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.careDocuments)}/*`}
            element={<StyledCareDocumentsRoutes />}
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`}
            element={<StyledHealthJourneyRoutes />}
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`}
            element={<HealthJourneyRoutes />}
          />
          <Route
            path={`${getModulePath(LEAGUE_MODULE_NAMES.healthProgress)}/*`}
            element={<HealthJourneyRoutes />}
          />
          <Route
            path={`${getModulePath(
              LEAGUE_MODULE_NAMES.healthJourneyHistory,
            )}/*`}
            element={<HealthJourneyRoutes />}
          />
          <Route
            path={ROUTE_PATHS.GENERIC_MASONRY_PAGE}
            element={
              <GenericMasonryPage
                pageWrapperComponent={GenericMasonryPageWrapper}
              />
            }
          />
          {/* <Route path="/pbp-change" element={<PBPChange/>} /> */}
          <Route element={<PageNotFoundErrorPage />} path="/*" />
        </Route>
      </Route>
    </Route>,
  ),
);
